import React, { useRef, useState } from "react";
import {
  AlertCircle,
  Award,
  Briefcase,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Mail,
  ShieldCheck,
  Upload,
} from "lucide-react";

// FormSubmit delivers to this email. The FIRST submission to a new email
// triggers an activation link sent to this inbox — click it once to
// activate, then all future submissions are delivered automatically.
const FORMSUBMIT_EMAIL = "hr1@nutralike.com";
const FORMSUBMIT_ENDPOINT = `https://formsubmit.co/${FORMSUBMIT_EMAIL}`;

// CC additional recipients — leave empty during initial activation.
// After hr1 is activated, you can add e.g.:
//   "marketing2@nutralike.com,marketing2@nutralike.com"
const CC_EMAILS = "";

// We submit to a hidden iframe (instead of fetch) because FormSubmit's
// AJAX endpoint silently drops file attachments. Using the regular endpoint
// with multipart/form-data is the only way the resume actually gets attached.
const IFRAME_NAME = "formsubmit-response-target";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx";

// Field names use spaces / title-case so they render cleanly in the
// FormSubmit email body (which displays the raw `name` attribute).
const INITIAL = {
  "First Name": "",
  "Last Name": "",
  "Email": "",
  "Phone Number": "",
};

const Applyjobform = () => {
  const fileInputRef = useRef(null);
  const iframeInitialLoad = useRef(true);

  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | submitting | success | error

  const fullName = `${form["First Name"].trim()} ${form["Last Name"].trim()}`.trim();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((errs) => {
        const next = { ...errs };
        delete next[name];
        return next;
      });
    }
    if (status.state === "error") setStatus({ state: "idle", msg: "" });
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    if (fieldErrors.file) {
      setFieldErrors((errs) => {
        const next = { ...errs };
        delete next.file;
        return next;
      });
    }
    if (status.state === "error") setStatus({ state: "idle", msg: "" });
  };

  const validate = () => {
    const errors = {};
    if (!form["First Name"].trim()) errors["First Name"] = "Please enter your first name.";
    else if (form["First Name"].trim().length < 2)
      errors["First Name"] = "First name must be at least 2 characters.";

    if (!form["Last Name"].trim()) errors["Last Name"] = "Please enter your last name.";

    if (!form["Email"].trim()) errors["Email"] = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form["Email"].trim()))
      errors["Email"] = "Please enter a valid email address.";

    if (!form["Phone Number"].trim()) errors["Phone Number"] = "Please enter your phone number.";
    else if (!/^[+\d][\d\s()-]{6,}$/.test(form["Phone Number"].trim()))
      errors["Phone Number"] = "Please enter a valid phone number.";

    if (!file) {
      errors.file = "Please attach your resume.";
    } else {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        errors.file = `File is too large. Maximum size is ${Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024))} MB.`;
      } else {
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["pdf", "doc", "docx"].includes(ext)) {
          errors.file = "Only PDF or Word documents are allowed.";
        }
      }
    }

    return errors;
  };

  const handleSubmit = (e) => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      e.preventDefault();
      setFieldErrors(errors);
      setStatus({
        state: "error",
        msg: "Please fix the highlighted fields and try again.",
      });
      return;
    }

    setFieldErrors({});
    setStatus({ state: "submitting", msg: "" });
    // Do NOT preventDefault — let the browser submit the form to the iframe.
  };

  const handleIframeLoad = () => {
    if (iframeInitialLoad.current) {
      iframeInitialLoad.current = false;
      return;
    }
    setStatus({
      state: "success",
      msg: "Thank you! Your application has been sent successfully. Our HR team will get back to you within 7 business days.",
    });
    setForm(INITIAL);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isBusy = status.state === "submitting";

  // Auto-reply text sent to the candidate after a successful submission.
  const autoresponseText =
    `Hi ${form["First Name"].trim() || "there"},\n\n` +
    `Thank you for applying to Nutra Like. We've received your application along with your resume, and our HR team will review it shortly.\n\n` +
    `If your profile matches an open role, we'll reach out to you at ${form["Email"].trim()} or ${form["Phone Number"].trim()}.\n\n` +
    `Best regards,\n` +
    `Nutra Like HR Team\n` +
    `www.nutralike.com`;

  const inputBase =
    "w-full px-4 py-3 rounded-lg border bg-white text-[#2f4f2f] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7c927b]/30 focus:border-[#7c927b] transition read-only:bg-gray-50 read-only:cursor-not-allowed";
  const inputNormal = "border-gray-200";
  const inputError = "border-red-400 focus:ring-red-200 focus:border-red-500";

  return (
    <section className="bg-[#F2F6EF] py-16 md:py-24 min-h-screen">
      {/* Hidden iframe: FormSubmit's response page loads here so the main
          page never navigates away after submission. */}
      <iframe
        name={IFRAME_NAME}
        title="FormSubmit response"
        onLoad={handleIframeLoad}
        style={{ display: "none" }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Hero / Heading */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#7c927b]" />
            <span className="text-[11px] sm:text-xs font-semibold tracking-[0.3em] text-[#7c927b] uppercase">
              Careers at Nutra Like
            </span>
            <span className="h-px w-10 bg-[#7c927b]" />
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-[#2f4f2f] mb-5 tracking-tight">
            Send Your Resume
          </h1>
          <p className="text-sm md:text-base text-[#2f4f2f]/70 max-w-2xl mx-auto leading-relaxed">
            We're always looking for passionate professionals to join our growing team. Submit your application below and our HR team will review your profile shortly.
          </p>
        </div>

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">

          {/* LEFT — info side */}
          <aside className="lg:col-span-2 space-y-8">

            <div>
              <h3 className="text-xs font-semibold tracking-[0.25em] text-[#7c927b] uppercase mb-5">
                What Happens Next
              </h3>
              <ol className="space-y-5">
                {[
                  { n: "1", t: "Submit your application", d: "Fill the form and upload your resume in PDF or Word format." },
                  { n: "2", t: "Our HR team reviews", d: "We'll carefully go through your profile within 7 business days." },
                  { n: "3", t: "We'll get in touch", d: "If shortlisted, our team will reach out to schedule the next steps." },
                ].map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-[#2f4f2f] text-white text-xs font-bold flex items-center justify-center shadow-sm">
                      {s.n}
                    </span>
                    <div className="pt-0.5">
                      <p className="font-semibold text-[#2f4f2f] text-sm">{s.t}</p>
                      <p className="text-xs text-[#2f4f2f]/60 mt-1 leading-relaxed">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="h-px bg-[#7c927b]/20" />

            <div className="space-y-3.5">
              {[
                { Icon: ShieldCheck, text: "Your information stays private and secure." },
                { Icon: Clock, text: "We respond to every application within 7 days." },
                { Icon: Award, text: "Equal opportunity employer — all backgrounds welcome." },
              ].map(({ Icon, text }, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-[#2f4f2f]/80">
                  <Icon className="w-4 h-4 text-[#7c927b] shrink-0 mt-0.5" />
                  <span>{text}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/70 backdrop-blur rounded-xl p-5 border border-[#7c927b]/20">
              <p className="text-[11px] font-semibold tracking-[0.25em] text-[#7c927b] uppercase mb-2">
                Need Help?
              </p>
              <p className="text-sm text-[#2f4f2f]/80 mb-3 leading-relaxed">
                Have questions about your application? Reach out to our HR team directly.
              </p>
              <a
                href="mailto:hr1@nutralike.com"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#2f4f2f] hover:text-[#1f3f1f] transition"
              >
                <Mail className="w-4 h-4" />
                <span className="underline underline-offset-2">hr1@nutralike.com</span>
              </a>
            </div>

          </aside>

          {/* RIGHT — form card */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(47,79,47,0.2)] border border-[#7c927b]/15 overflow-hidden">

              {/* Card header */}
              <div className="px-6 sm:px-8 pt-6 pb-5 border-b border-[#7c927b]/10 bg-gradient-to-b from-white to-[#F2F6EF]/40">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#7c927b]/15">
                    <Briefcase className="w-4 h-4 text-[#2f4f2f]" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-[#2f4f2f]">Application Form</h2>
                    <p className="text-[11px] text-[#2f4f2f]/60 mt-0.5">All fields are required</p>
                  </div>
                </div>
              </div>

              {/* Form — submits directly to FormSubmit with multipart/form-data
                  so the resume file is properly attached to the email. */}
              <form
                action={FORMSUBMIT_ENDPOINT}
                method="POST"
                encType="multipart/form-data"
                target={IFRAME_NAME}
                onSubmit={handleSubmit}
                className="px-6 sm:px-8 py-6 space-y-5"
                noValidate
              >
                {/* FormSubmit special fields — hidden from the email body. */}
                <input type="hidden" name="_subject" value={`New Job Application — ${fullName || "Applicant"}`} />
                <input type="hidden" name="_replyto" value={form["Email"].trim()} />
                <input type="hidden" name="_template" value="box" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_autoresponse" value={autoresponseText} />
                {CC_EMAILS && <input type="hidden" name="_cc" value={CC_EMAILS} />}
                {/* Sender display name in the FormSubmit email header */}
                <input type="hidden" name="name" value={fullName} />

                {/* First + Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first-name" className="block text-sm font-medium text-[#2f4f2f] mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="first-name"
                      type="text"
                      name="First Name"
                      value={form["First Name"]}
                      onChange={handleChange}
                      readOnly={isBusy}
                      placeholder="John"
                      aria-invalid={!!fieldErrors["First Name"]}
                      className={`${inputBase} ${fieldErrors["First Name"] ? inputError : inputNormal}`}
                    />
                    {fieldErrors["First Name"] && (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors["First Name"]}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="last-name" className="block text-sm font-medium text-[#2f4f2f] mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="last-name"
                      type="text"
                      name="Last Name"
                      value={form["Last Name"]}
                      onChange={handleChange}
                      readOnly={isBusy}
                      placeholder="Doe"
                      aria-invalid={!!fieldErrors["Last Name"]}
                      className={`${inputBase} ${fieldErrors["Last Name"] ? inputError : inputNormal}`}
                    />
                    {fieldErrors["Last Name"] && (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors["Last Name"]}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#2f4f2f] mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="Email"
                    value={form["Email"]}
                    onChange={handleChange}
                    readOnly={isBusy}
                    placeholder="you@example.com"
                    aria-invalid={!!fieldErrors["Email"]}
                    className={`${inputBase} ${fieldErrors["Email"] ? inputError : inputNormal}`}
                  />
                  {fieldErrors["Email"] && (
                    <p className="mt-1.5 text-xs text-red-600">{fieldErrors["Email"]}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#2f4f2f] mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    name="Phone Number"
                    value={form["Phone Number"]}
                    onChange={handleChange}
                    readOnly={isBusy}
                    placeholder="+91 98765 43210"
                    aria-invalid={!!fieldErrors["Phone Number"]}
                    className={`${inputBase} ${fieldErrors["Phone Number"] ? inputError : inputNormal}`}
                  />
                  {fieldErrors["Phone Number"] && (
                    <p className="mt-1.5 text-xs text-red-600">{fieldErrors["Phone Number"]}</p>
                  )}
                </div>

                {/* Resume upload */}
                <div>
                  <label className="block text-sm font-medium text-[#2f4f2f] mb-1.5">
                    Resume <span className="text-red-500">*</span>
                  </label>
                  <label
                    className={`group flex flex-col items-center justify-center gap-2 w-full px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer transition ${
                      fieldErrors.file
                        ? "border-red-400 bg-red-50/40"
                        : file
                        ? "border-[#7c927b] bg-[#F2F6EF]/60"
                        : "border-[#7c927b]/40 bg-gray-50 hover:border-[#7c927b] hover:bg-[#F2F6EF]/60"
                    } ${isBusy ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full transition ${
                        file ? "bg-[#7c927b] text-white" : "bg-[#7c927b]/15 text-[#2f4f2f] group-hover:bg-[#7c927b] group-hover:text-white"
                      }`}
                    >
                      {file ? <FileText className="w-5 h-5" /> : <Upload className="w-5 h-5" />}
                    </div>
                    {file ? (
                      <>
                        <p className="text-sm font-medium text-[#2f4f2f] text-center break-all px-2">
                          {file.name}
                        </p>
                        <p className="text-xs text-[#2f4f2f]/60">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB · Click to change
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-[#2f4f2f]">
                          Click to upload your resume
                        </p>
                        <p className="text-xs text-[#2f4f2f]/60">
                          PDF, DOC or DOCX · Max {Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024))} MB
                        </p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="attachment"
                      accept={ACCEPTED_FILE_TYPES}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  {fieldErrors.file && (
                    <p className="mt-1.5 text-xs text-red-600">{fieldErrors.file}</p>
                  )}
                </div>

                {/* Success banner */}
                {status.state === "success" && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex items-start gap-2.5 text-sm text-green-800 bg-green-50 border border-green-200 px-4 py-3 rounded-lg"
                  >
                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-green-600" />
                    <span>{status.msg}</span>
                  </div>
                )}

                {/* Error banner */}
                {status.state === "error" && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg"
                  >
                    <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-red-600" />
                    <span>{status.msg}</span>
                  </div>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isBusy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#2f4f2f] text-white font-semibold px-8 py-3.5 rounded-lg hover:bg-[#3d653d] active:bg-[#243f24] transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isBusy && <Loader2 className="w-5 h-5 animate-spin" />}
                  {isBusy ? "Sending Application..." : "Submit Application"}
                </button>

                <p className="text-xs text-center text-[#2f4f2f]/50 pt-1">
                  By submitting, you agree that Nutra Like may contact you about your application.
                </p>
              </form>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default Applyjobform;
